import { Card, Text, tokens } from "@fluentui/react-components";

import { StatusBadge } from "@/components/StatusBadge";
import { useDocumentTypes } from "@/features/documents/useDocumentTypes";
import { formatDocumentType } from "@/lib/formatDocumentType";
import type { EmailSummary } from "@/types/domain";

export interface EmailCardProps {
  email: EmailSummary;
  onClick?: (email: EmailSummary) => void;
}

const DATE_TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: true,
});

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return DATE_TIME_FMT.format(new Date(iso));
  } catch {
    return iso;
  }
}

export function EmailCard({ email, onClick }: EmailCardProps) {
  const { data: documentTypes } = useDocumentTypes();
  const humanTypes = email.documentTypes
    .map((key) => formatDocumentType(key, documentTypes))
    .join(", ");
  return (
    <Card
      onClick={onClick ? () => onClick(email) : undefined}
      style={{ cursor: onClick ? "pointer" : "default", minWidth: 0 }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gridTemplateRows: "auto auto",
          columnGap: 12,
          rowGap: 4,
          alignItems: "start",
        }}
      >
        <Text
          weight="semibold"
          size={400}
          style={{
            gridColumn: 1,
            gridRow: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={email.subject || ""}
        >
          {email.subject || "(no subject)"}
        </Text>
        <div
          style={{
            gridColumn: 2,
            gridRow: 1,
            justifySelf: "end",
            alignSelf: "center",
          }}
        >
          <StatusBadge status={email.aggregateStatus} />
        </div>
        <div
          style={{
            gridColumn: "1 / -1",
            gridRow: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
          }}
        >
          <Text
            size={200}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span style={{ color: tokens.colorNeutralForeground2 }}>From </span>
            {email.from || "(unknown)"}
            <span style={{ color: tokens.colorNeutralForeground2 }}> · </span>
            {formatDate(email.receivedAt)}
          </Text>
          <Text
            size={200}
            style={{
              color: tokens.colorNeutralForeground2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {email.documentCount} document
            {email.documentCount === 1 ? "" : "s"}
            {humanTypes ? ` · ${humanTypes}` : ""}
          </Text>
        </div>
      </div>
    </Card>
  );
}
